/**
 * useAuth Hook
 * AIDLC Administrative Backend
 *
 * In production: wraps MSAL React hooks for authentication state.
 * In dev bypass mode: returns a mock admin user so the app is fully usable.
 */

import { useCallback, useMemo } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { AppRoles, loginRequest, type AppRole } from '../authConfig';

const DEV_AUTH_BYPASS = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  roles: AppRole[];
}

/**
 * Dev bypass mock user -- Neo as Admin
 */
const DEV_USER: AuthUser = {
  id: 'dev-neo-001',
  name: 'Neo (Dev Mode)',
  email: 'dev@example.invalid',
  roles: [AppRoles.ADMIN],
};

export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] ?? null);

  if (DEV_AUTH_BYPASS) {
    return {
      user: DEV_USER,
      isAuthenticated: true,
      isLoading: false,
      isAdmin: true,
      isOperator: true,
      login: async () => { console.warn('[Auth] Dev bypass -- login is a no-op'); },
      logout: async () => { console.warn('[Auth] Dev bypass -- logout is a no-op'); },
      hasRole: (_role: AppRole) => true,
    };
  }

  const isAuthenticated = accounts.length > 0;
  const isLoading = inProgress !== 'none';

  const user: AuthUser | null = useMemo(() => {
    if (!account) return null;

    const claims = account.idTokenClaims as Record<string, unknown> | undefined;
    const roles = (claims?.roles as string[] | undefined) ?? [];

    return {
      id: account.localAccountId,
      name: account.name ?? 'Unknown',
      email: account.username,
      roles: roles.filter(
        (r): r is AppRole =>
          r === AppRoles.ADMIN || r === AppRoles.OPERATOR || r === AppRoles.VIEWER
      ),
    };
  }, [account]);

  const isAdmin = useMemo(
    () => user?.roles.includes(AppRoles.ADMIN) ?? false,
    [user]
  );

  const isOperator = useMemo(
    () => user?.roles.includes(AppRoles.OPERATOR) ?? false,
    [user]
  );

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('[Auth] Login failed:', error);
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      });
    } catch (error) {
      console.error('[Auth] Logout failed:', error);
    }
  }, [instance]);

  const hasRole = useCallback(
    (role: AppRole): boolean => {
      return user?.roles.includes(role) ?? false;
    },
    [user]
  );

  return {
    user,
    isAuthenticated,
    isLoading,
    isAdmin,
    isOperator,
    login,
    logout,
    hasRole,
  };
}
